import { Skeleton } from "primereact/skeleton";
import { useMemo } from "react";

import { PathCard } from "sj-ui-toolkit/src/atlas/components/ContentCard/PathCard/PathCard";
import { useLevelContext } from "sj-ui-toolkit/src/atlas/contexts/Level/LevelContextProvider";

const loadingSkeleton = Array.from({ length: 4 }, (_, i) => `skelly-${i}`);

const LevelRow = ({ metadata, details, variant, currentPaths = [] }) => {
  const { isLoadingLevelDetails } = useLevelContext();

  if (details?.error) {
    return null;
  }

  // Filter out paths that the user is already enrolled in based on roleId
  const filteredDetails = useMemo(() => {
    if (!currentPaths?.length || !details?.length) {
      return details;
    }

    // Create a Set of enrolled roleIds for fast lookup
    const enrolledRoleIds = new Set(
      currentPaths.filter(path => path.roleId != null).map(path => path.roleId)
    );

    // Filter out paths where the role.id matches an enrolled roleId
    return details.filter(path => {
      const pathRoleId = path?.id;
      return pathRoleId == null || !enrolledRoleIds.has(pathRoleId);
    });
  }, [details, currentPaths]);

  const isLoading =
    isLoadingLevelDetails ||
    details === undefined ||
    (details !== undefined && filteredDetails === undefined);

  // Remove levels with no available paths
  if (!isLoading && filteredDetails?.length === 0) {
    return null;
  }

  // SHRPA-2293 -- Variant will be stored at "Level" level, until then this logic lives here
  const getVariantName = variant => {
    switch (variant) {
      case 0:
        return "indigo";
      case 1:
        return "blue";
      case 2:
        return "teal";
      case 3:
      default:
        return "mauve";
    }
  };
  const variantName = getVariantName(variant);

  return (
    <div className="mb-6">
      <h5 className={"atlas-h5 pb-2"}>{metadata.name}</h5>
      <p>{metadata.description}</p>
      <div className="overflow-x-auto flex gap-6 py-6">
        {isLoading ? (
          <>
            {loadingSkeleton.map(skel => (
              <Skeleton
                key={`${metadata.id}-${skel}`}
                width="300px"
                height="400px"
                className="mr-6"
              />
            ))}
          </>
        ) : (
          <>
            {filteredDetails?.map(path => (
              <PathCard
                key={path.id}
                cardSize="small"
                variant={variantName}
                id={path.id}
                name={path.name}
                description={path.description}
                lessonsPassed={metadata.status}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const LevelPaths = ({ currentPaths, hasOutstandingStrictAssignment = false }) => {
  const { levels, levelsLoading, levelDetails } = useLevelContext();
  if (levelsLoading || hasOutstandingStrictAssignment) {
    return null;
  }

  return (
    <div className="my-8">
      {levels.map((level, index) => (
        <LevelRow
          key={level.id}
          metadata={level}
          details={levelDetails[level.id]}
          variant={index % 4}
          currentPaths={currentPaths}
        />
      ))}
    </div>
  );
};
